import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { encryptionService } from "../config/axiosConfig";
import { RouteConstants } from "../constants/route-constant";
import { useSelector } from "react-redux";
import { EncryptionService, STORAGE_KEYS } from "../services/encrypt-decrypt";

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const fhirState = useSelector((state: any) => state.fhir);

  const publicRoutes = [RouteConstants.ROUTE_HOME, "/launch", "/unauthorized"];

  const isPublicRoute = publicRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(route + "/")
  );

  if (isPublicRoute) return children;

  if (fhirState?.hasFailed) {
    return <Navigate to={RouteConstants.ROUTE_UNAUTHORIZED} replace />;
  }

  // Check if access token expired
  const isAccessTokenExpired = (): boolean => {
    const expiry = localStorage.getItem(STORAGE_KEYS.EXPIRES_IN);
    if (!expiry) return true; // treat missing expiry as expired
    return Date.now() >= Number(expiry);
  };

  // Check authentication
  const isAuthenticated = (): boolean => {
    const token = encryptionService.getJwtToken();

    if (!token) return false;

    if (isAccessTokenExpired()) {
      console.warn("Access token expired, clearing tokens");
      if (
        typeof (encryptionService as EncryptionService)?.clearTokens ===
          "function" &&
        typeof (encryptionService as EncryptionService)?.reset === "function"
      ) {
        try {
          (encryptionService as EncryptionService).clearTokens();
          (encryptionService as EncryptionService).reset();
        } catch {
          // ignore errors
        }
      }
      return false;
    }

    const keyId = localStorage.getItem(STORAGE_KEYS.KEY_ID);
    if (keyId) return true;

    // FHIR + JWT established on /home; encryption key may still be initializing
    return Boolean(fhirState?.isInitialized && !fhirState?.hasFailed);
  };

  if (!isAuthenticated()) {
    return <Navigate to={RouteConstants.ROUTE_UNAUTHORIZED} replace />;
  }

  return children;
};

export default ProtectedRoute;
